import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box, Paper, Typography, Button, IconButton, Tabs, Tab,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Chip, Avatar, LinearProgress, Tooltip,
  Alert, CircularProgress, Card, CardContent
} from '@mui/material';
import {
  PlayArrow, Stop, Add, Delete, GetApp,
  AccessTime, Timer as TimerIcon, Person, Assignment
} from '@mui/icons-material';
import { timeTrackingService } from '../services/api';

const fmtDuration = (secs) => {
  if (!secs || secs < 0) return '00:00:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  return `${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
};

const fmtHours = (h) => {
  if (!h || h === 0) return '0h';
  const hrs = Math.floor(parseFloat(h));
  const mins = Math.round((parseFloat(h) - hrs) * 60);
  if (mins === 0) return `${hrs}h`;
  return `${hrs}h${String(mins).padStart(2,'0')}`;
};

export default function TimeTracker({ projectId, currentUserId }) {
  const [report, setReport]           = useState(null);
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsed, setElapsed]         = useState(0);
  const [innerTab, setInnerTab]       = useState(0);
  const [logDialog, setLogDialog]     = useState(false);
  const [logTask, setLogTask]         = useState(null);
  const [logForm, setLogForm]         = useState({
    hours: '', description: '', date: new Date().toISOString().split('T')[0]
  });
  const [loading, setLoading]         = useState(true);
  const [timerBusy, setTimerBusy]     = useState(false);
  const [error, setError]             = useState('');
  const [success, setSuccess]         = useState('');
  const intervalRef = useRef(null);

  const showOk  = (m) => { setSuccess(m); setTimeout(() => setSuccess(''), 3000); };
  const showErr = (m) => { setError(m);   setTimeout(() => setError(''),   4000); };

  const fetchReport = useCallback(async () => {
    try {
      const res = await timeTrackingService.getReport(projectId);
      setReport(res.data);
      setActiveTimer(res.data.activeTimer || null);
    } catch {
      showErr('Erreur lors du chargement du rapport de temps');
    } finally {
      setLoading(false);
    }
  }, [projectId]);

  useEffect(() => { fetchReport(); }, [fetchReport]);

  // Tick elapsed seconds
  useEffect(() => {
    clearInterval(intervalRef.current);
    if (activeTimer?.started_at) {
      const tick = () => {
        const ms = Date.now() - new Date(activeTimer.started_at).getTime();
        setElapsed(Math.max(0, Math.floor(ms / 1000)));
      };
      tick();
      intervalRef.current = setInterval(tick, 1000);
    } else {
      setElapsed(0);
    }
    return () => clearInterval(intervalRef.current);
  }, [activeTimer]);

  const handleStartTimer = async (task) => {
    setTimerBusy(true);
    try {
      const res = await timeTrackingService.startTimer(task.task_id || task.id);
      setActiveTimer({ ...res.data.session, task_title: task.title });
      showOk(`Chronomètre démarré — ${task.title}`);
    } catch (err) {
      showErr(err.response?.data?.error || 'Erreur démarrage chronomètre');
    } finally {
      setTimerBusy(false);
    }
  };

  const handleStopTimer = async () => {
    setTimerBusy(true);
    try {
      await timeTrackingService.stopTimer(activeTimer.task_id);
      setActiveTimer(null);
      showOk('Chronomètre arrêté — temps enregistré');
      fetchReport();
    } catch (err) {
      showErr(err.response?.data?.error || 'Erreur arrêt chronomètre');
    } finally {
      setTimerBusy(false);
    }
  };

  const handleOpenLog = (task) => {
    setLogTask(task);
    setLogForm({ hours: '', description: '', date: new Date().toISOString().split('T')[0] });
    setLogDialog(true);
  };

  const handleLogSubmit = async () => {
    const h = parseFloat(logForm.hours);
    if (!logForm.hours || isNaN(h) || h <= 0) {
      showErr('Veuillez saisir un nombre d\'heures valide (ex: 1.5)');
      return;
    }
    try {
      await timeTrackingService.createEntry({
        task_id:     logTask.task_id || logTask.id,
        hours:       h,
        description: logForm.description,
        entry_date:  logForm.date
      });
      setLogDialog(false);
      showOk('Heures enregistrées');
      fetchReport();
    } catch (err) {
      showErr(err.response?.data?.error || 'Erreur enregistrement');
    }
  };

  const handleDeleteEntry = async (entryId) => {
    try {
      await timeTrackingService.deleteEntry(entryId);
      fetchReport();
    } catch (err) {
      showErr(err.response?.data?.error || 'Erreur suppression');
    }
  };

  const handleExport = async () => {
    try {
      const res = await timeTrackingService.exportTimesheet(projectId);
      const blob = new Blob([res.data], { type: 'text/csv;charset=utf-8' });
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = `timesheet_projet_${projectId}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      showErr('Erreur lors de l\'export CSV');
    }
  };

  if (loading) return (
    <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
      <CircularProgress />
    </Box>
  );

  const summary      = report?.summary  || {};
  const reportTasks  = report?.tasks    || [];
  const members      = report?.members  || [];
  const entries      = report?.entries  || [];
  const totalEst     = parseFloat(summary.total_estimated) || 0;
  const totalLog     = parseFloat(summary.total_logged)    || 0;
  const gap          = totalEst - totalLog;
  const pctTime      = totalEst > 0 ? Math.round((totalLog / totalEst) * 100) : null;
  const maxMemberHrs = members.length ? Math.max(...members.map(m => parseFloat(m.total_logged) || 0), 1) : 1;

  return (
    <Box>
      {error   && <Alert severity="error"   onClose={() => setError('')}   sx={{ mb: 2 }}>{error}</Alert>}
      {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

      {/* Active Timer Banner */}
      {activeTimer && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'primary.main', color: 'primary.contrastText', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            <TimerIcon />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="caption" sx={{ opacity: 0.8 }}>Chronomètre actif</Typography>
              <Typography variant="body2" fontWeight="bold" noWrap>{activeTimer.task_title}</Typography>
            </Box>
            <Typography variant="h6" fontFamily="monospace" sx={{ letterSpacing: 2 }}>
              {fmtDuration(elapsed)}
            </Typography>
            <Button
              variant="outlined" color="inherit" size="small"
              startIcon={<Stop />}
              onClick={handleStopTimer}
              disabled={timerBusy}
            >
              Arrêter
            </Button>
          </Box>
        </Paper>
      )}

      {/* Summary Cards */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 3 }}>
        {[
          { label: 'Heures estimées', value: fmtHours(totalEst), color: 'text.primary' },
          { label: 'Heures loguées',  value: fmtHours(totalLog), color: 'primary.main' },
          { label: 'Écart',           value: `${gap >= 0 ? '+' : ''}${fmtHours(gap)}`, color: gap < 0 ? 'error.main' : 'success.main' },
          { label: 'Avancement temps', value: pctTime !== null ? `${pctTime}%` : '—', color: 'text.primary' },
        ].map(({ label, value, color }) => (
          <Card key={label} elevation={1} sx={{ border: '1px solid', borderColor: 'divider' }}>
            <CardContent sx={{ pb: '12px !important' }}>
              <Typography variant="caption" color="text.secondary">{label}</Typography>
              <Typography variant="h5" fontWeight="bold" color={color}>{value}</Typography>
            </CardContent>
          </Card>
        ))}
      </Box>

      {/* Inner Tabs */}
      <Paper elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, overflow: 'hidden' }}>
        <Box sx={{ borderBottom: '1px solid', borderColor: 'divider' }}>
          <Tabs value={innerTab} onChange={(_, v) => setInnerTab(v)}>
            <Tab label="Tâches"      icon={<Assignment fontSize="small" />} iconPosition="start" />
            <Tab label="Par membre"  icon={<Person     fontSize="small" />} iconPosition="start" />
            <Tab label="Journal"     icon={<AccessTime fontSize="small" />} iconPosition="start" />
          </Tabs>
        </Box>

        {/* Tab 0 — Task list */}
        {innerTab === 0 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'action.hover' } }}>
                  <TableCell>Tâche</TableCell>
                  <TableCell align="right">Estimé</TableCell>
                  <TableCell align="right">Logué</TableCell>
                  <TableCell sx={{ minWidth: 130 }}>Progression</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {reportTasks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      Aucune tâche dans ce projet
                    </TableCell>
                  </TableRow>
                ) : reportTasks.map((task) => {
                  const isActive = activeTimer?.task_id === task.task_id;
                  const est      = parseFloat(task.estimated_hours) || 0;
                  const logged   = parseFloat(task.logged_hours)    || 0;
                  const pct      = est > 0 ? Math.min(100, Math.round((logged / est) * 100)) : null;
                  const over     = est > 0 && logged > est;

                  return (
                    <TableRow key={task.task_id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="body2">{task.title}</Typography>
                          {isActive && (
                            <Chip label="⏱" size="small" color="primary" sx={{ fontSize: '0.7rem', height: 18 }} />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" color="text.secondary">
                          {est > 0 ? fmtHours(est) : '—'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold" color={over ? 'error.main' : 'text.primary'}>
                          {fmtHours(logged)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {pct !== null ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <LinearProgress
                              variant="determinate"
                              value={Math.min(100, pct)}
                              color={pct >= 100 ? 'error' : pct >= 80 ? 'warning' : 'primary'}
                              sx={{ flex: 1, height: 6, borderRadius: 3 }}
                            />
                            <Typography variant="caption" sx={{ minWidth: 32 }}>{pct}%</Typography>
                          </Box>
                        ) : (
                          <Typography variant="caption" color="text.secondary">—</Typography>
                        )}
                      </TableCell>
                      <TableCell align="right">
                        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 0.5 }}>
                          <Tooltip title={isActive ? 'Arrêter le chronomètre' : (activeTimer ? 'Arrêtez le chrono actuel d\'abord' : 'Démarrer le chronomètre')}>
                            <span>
                              <IconButton
                                size="small"
                                color={isActive ? 'error' : 'primary'}
                                onClick={() => isActive ? handleStopTimer() : handleStartTimer(task)}
                                disabled={timerBusy || (!!activeTimer && !isActive)}
                              >
                                {isActive ? <Stop fontSize="small" /> : <PlayArrow fontSize="small" />}
                              </IconButton>
                            </span>
                          </Tooltip>
                          <Tooltip title="Loguer des heures manuellement">
                            <IconButton size="small" onClick={() => handleOpenLog(task)}>
                              <Add fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab 1 — By member */}
        {innerTab === 1 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'action.hover' } }}>
                  <TableCell>Membre</TableCell>
                  <TableCell align="right">Heures loguées</TableCell>
                  <TableCell align="right">Tâches</TableCell>
                  <TableCell sx={{ minWidth: 150 }}>Proportion</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {members.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      Aucune heure loguée pour le moment
                    </TableCell>
                  </TableRow>
                ) : members.map((m) => {
                  const pct = Math.round((parseFloat(m.total_logged) / maxMemberHrs) * 100);
                  return (
                    <TableRow key={m.user_id} hover sx={{ '&:last-child td': { border: 0 } }}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                          <Avatar sx={{ width: 30, height: 30, fontSize: '0.8rem', bgcolor: 'primary.main' }}>
                            {m.user_name?.charAt(0).toUpperCase()}
                          </Avatar>
                          <Typography variant="body2">{m.user_name}</Typography>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">{fmtHours(m.total_logged)}</Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">{m.task_count}</Typography>
                      </TableCell>
                      <TableCell>
                        <LinearProgress variant="determinate" value={pct} sx={{ height: 6, borderRadius: 3 }} />
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Tab 2 — Journal */}
        {innerTab === 2 && (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow sx={{ '& th': { fontWeight: 'bold', bgcolor: 'action.hover' } }}>
                  <TableCell>Date</TableCell>
                  <TableCell>Membre</TableCell>
                  <TableCell>Tâche</TableCell>
                  <TableCell align="right">Heures</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell align="right" />
                </TableRow>
              </TableHead>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                      Aucune entrée de temps pour le moment
                    </TableCell>
                  </TableRow>
                ) : entries.map((e) => (
                  <TableRow key={e.id} hover sx={{ '&:last-child td': { border: 0 } }}>
                    <TableCell>
                      <Typography variant="caption">
                        {e.entry_date ? new Date(e.entry_date).toLocaleDateString('fr-FR') : '—'}
                      </Typography>
                    </TableCell>
                    <TableCell><Typography variant="body2">{e.user_name}</Typography></TableCell>
                    <TableCell>
                      <Typography variant="body2" sx={{ maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {e.task_title}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Chip label={fmtHours(e.hours)} size="small" color="primary" variant="outlined" />
                    </TableCell>
                    <TableCell>
                      <Typography variant="caption" color="text.secondary">{e.description || '—'}</Typography>
                    </TableCell>
                    <TableCell align="right">
                      {e.user_id === currentUserId && (
                        <Tooltip title="Supprimer cette entrée">
                          <IconButton size="small" color="error" onClick={() => handleDeleteEntry(e.id)}>
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Export button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
        <Button
          variant="outlined"
          startIcon={<GetApp />}
          onClick={handleExport}
          disabled={entries.length === 0}
        >
          Exporter feuille de temps (CSV)
        </Button>
      </Box>

      {/* Log Hours Dialog */}
      <Dialog open={logDialog} onClose={() => setLogDialog(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Loguer des heures</DialogTitle>
        <DialogContent>
          {logTask && (
            <Chip label={logTask.title} size="small" sx={{ mb: 2, mt: 0.5 }} />
          )}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Heures"
              type="number"
              value={logForm.hours}
              onChange={(e) => setLogForm({ ...logForm, hours: e.target.value })}
              inputProps={{ min: 0.1, step: 0.25 }}
              autoFocus
              fullWidth
              helperText="Exemples : 0.5 = 30 min · 1.5 = 1h30 · 2 = 2h"
              size="small"
            />
            <TextField
              label="Date"
              type="date"
              value={logForm.date}
              onChange={(e) => setLogForm({ ...logForm, date: e.target.value })}
              fullWidth
              size="small"
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Description (optionnel)"
              value={logForm.description}
              onChange={(e) => setLogForm({ ...logForm, description: e.target.value })}
              multiline
              rows={2}
              fullWidth
              size="small"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogDialog(false)}>Annuler</Button>
          <Button variant="contained" onClick={handleLogSubmit}>Enregistrer</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
